/**
 * FundMatch API Service
 * Handles all API calls for P1: FundMatch (Grant Discovery)
 */
import apiClient from '../lib/apiClient';

/**
 * Get FundMatch product information
 */
export const getFundMatchInfo = async () => {
  const response = await apiClient.get('/fundmatch/');
  return response.data;
};

/**
 * List grants with filters
 * @param {Object} filters - Filter parameters
 * @param {string} filters.funder_name - Filter by funder name
 * @param {number} filters.min_amount - Minimum grant amount
 * @param {number} filters.max_amount - Maximum grant amount
 * @param {string} filters.source - Source (grantconnect, government, etc)
 * @param {number} filters.limit - Results limit (max 100)
 * @param {number} filters.offset - Pagination offset
 */
export const listGrants = async (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.funder_name) params.append('funder_name', filters.funder_name);
  if (filters.min_amount) params.append('min_amount', filters.min_amount);
  if (filters.max_amount) params.append('max_amount', filters.max_amount);
  if (filters.source) params.append('source', filters.source);
  if (filters.limit) params.append('limit', filters.limit);
  if (filters.offset) params.append('offset', filters.offset);

  const response = await apiClient.get(`/fundmatch/grants?${params.toString()}`);
  return response.data;
};

/**
 * Search grants by keyword
 * @param {string} query - Search query
 * @param {number} limit - Results limit (max 100)
 */
export const searchGrants = async (query, limit = 20) => {
  const response = await apiClient.get(`/fundmatch/search`, {
    params: { query, limit }
  });
  return response.data;
};

/**
 * Get grant matches for a specific charity
 * Uses 3-tier matching engine (Hard filters -> Soft scoring -> Semantic similarity)
 * Pass project_description to activate v8 HyDE + GraphRAG rationale generation.
 * @param {string} charityBN - Charity Business Number
 * @param {string} [projectDescription] - Optional project description to activate HyDE + GraphRAG
 */
export const getGrantMatches = async (charityBN, projectDescription) => {
  const params = projectDescription ? { project_description: projectDescription } : {};
  const response = await apiClient.get(`/fundmatch/match/${charityBN}`, { params });
  return response.data;
};

/**
 * Get 8 measures for a charity
 * M1: Opportunities ($), M2: Expectations ($), M3: Survey ($/entity), M4: Outlook (Δ%)
 * M5: Leverage (0-1), M6: Positioning (%ile), M7: Efficiency ($/hr), M8: Assurance ($)
 * @param {string} charityBN - Charity Business Number
 */
export const getCharityMeasures = async (charityBN) => {
  const response = await apiClient.get(`/fundmatch/measures/${charityBN}`);
  return response.data;
};

/**
 * Get Tier A default search matches (v9.4 — province-filtered, geo-gravity scored)
 * @param {string} charityBN - Charity Business Number
 * @param {number} limit - Number of results (default 8)
 */
export const getDefaultSearchMatches = async (charityBN, limit = 8) => {
  const response = await apiClient.get(`/v94/default-search/${charityBN}`, { params: { limit } });
  return response.data;
};

/**
 * List Canadian charities with filters
 * @param {Object} filters - Filter parameters
 * @param {string} filters.province - Province filter
 * @param {string} filters.city - City filter
 * @param {string} filters.designation - Designation code filter
 * @param {number} filters.limit - Results limit (max 100)
 * @param {number} filters.offset - Pagination offset
 */
export const listCharities = async (filters = {}) => {
  const params = new URLSearchParams();

  if (filters.province) params.append('province', filters.province);
  if (filters.city) params.append('city', filters.city);
  if (filters.designation) params.append('designation', filters.designation);
  if (filters.limit) params.append('limit', filters.limit);
  if (filters.offset) params.append('offset', filters.offset);

  const response = await apiClient.get(`/fundmatch/charities?${params.toString()}`);
  return response.data;
};

// ─── Competition API ──────────────────────────────────────────────────────────

export const getCompetitionSummary = async (bn) => {
  const response = await apiClient.get(`/competition/summary/${bn}`);
  return response.data;
};

export const getDirectCompetitors = async (bn, limit = 20) => {
  const response = await apiClient.get(`/competition/direct/${bn}?limit=${limit}`);
  return response.data;
};

export const getCompetitionLandscape = async (bn, maxPerStage = 10) => {
  const response = await apiClient.get(`/competition/landscape/${bn}?max_per_stage=${maxPerStage}`);
  return response.data;
};

export const getGrantCompetition = async (bn, grantId) => {
  const response = await apiClient.get(`/competition/grant/${bn}/${grantId}`);
  return response.data;
};
